// "Not acquired" (2026-10-06): undo a wrong acquisition.
//
// Company sold is a final exit — the deal page offers no Reopen — and the
// company page's un-mark used to clear the flag without touching the deals it
// had exited. When the acquisition turns out to be wrong (K&H Home Solutions
// was "acquired by D5 Capital Holdings", its owner's own holding company) this
// drawer is the way back, and it will not submit without a reason:
//
//   GET  /api/companies/{id}/acquisition-reversal   what it would undo (preview)
//   POST /api/companies/{id}/acquisition-reversal   do it; { note } is required
//
// One drawer for both entry points — the deal page (an exited deal's header)
// and the company page (the Acquired row's un-mark). It works on the COMPANY:
// every deal exited as Company sold reopens at the stage it left, the acquired
// flag clears, the acquisition record is rejected, and the note is filed.

// Same count the server uses: whitespace runs collapse before the minimum.
const reasonLength = (s) => (s || '').trim().split(/\s+/).filter(Boolean).join(' ').length;

// `zIndex` (optional) stacks it over an open reference drawer / workspace (the v2 record pages pass theirs).
const ReverseAcquisitionDrawer = ({ open, companyId, companyName, onClose, onDone, zIndex }) => {
  const [plan, setPlan] = React.useState(null);
  const [loadErr, setLoadErr] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!open || !companyId) return undefined;
    let alive = true;
    setPlan(null); setLoadErr(null); setNote(''); setBusy(false); setErr(null);
    window.AtlasApi.getAcquisitionReversal(companyId)
      .then((p) => { if (alive) setPlan(p); })
      .catch((e) => { if (alive) setLoadErr(e?.message || 'Could not load'); });
    return () => { alive = false; };
  }, [open, companyId]);

  const minNote = plan?.min_note || 15;
  const len = reasonLength(note);
  const tooShort = len < minNote;
  const blocked = plan?.blocked === 'sold_through_us';
  const canSubmit = !!plan && plan.can_reverse && !blocked && !tooShort && !busy;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const out = await window.AtlasApi.reverseAcquisition(companyId, { note: note.trim() });
      const n = (out?.reopened || []).length;
      window.pushToast?.({
        type: 'success', title: 'Marked not acquired',
        body: n ? `${n} deal${n === 1 ? '' : 's'} reopened and the reason is on the record.` : 'The reason is on the record.',
      });
      if (onDone) await onDone(out);
    } catch (e) {
      // 422 note_required / 409 sold_through_us arrive as "<code>: <message>".
      setErr((e?.message || 'Could not reverse').replace(/^\d+\s*/, '').replace(/^[a-z_]+:\s*/, ''));
      setBusy(false);
    }
  };

  const rose = 'var(--rose-fg, oklch(0.50 0.14 25))';
  const box = { padding: '10px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8 };
  const label = { fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };

  return (
    <window.Drawer
      open={open}
      onClose={busy ? undefined : onClose}
      width={520}
      zIndex={zIndex}
      title="Not acquired"
      subtitle={`${companyName || plan?.company?.name || 'This company'} — undo the acquisition`}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: err ? rose : 'var(--text-4)' }}>
            {err || (blocked ? '' : (tooShort ? `A reason is required — ${len}/${minNote} characters.` : ''))}
          </span>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <window.Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>{blocked ? 'Close' : 'Cancel'}</window.Btn>
            {!blocked && (
              <window.Btn size="sm" variant="primary" disabled={!canSubmit} onClick={submit}>
                {busy ? 'Reversing…' : 'Reopen & record reason'}
              </window.Btn>
            )}
          </span>
        </div>
      )}
    >
      {loadErr && <div style={{ fontSize: 12.5, color: rose }}>{loadErr}</div>}
      {!plan && !loadErr && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>}

      {blocked && (
        <div style={{ ...box, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' }}>
          <b>{plan.won_deals[0]?.name}</b> is Won, so this company was sold through IndustryPro and the
          acquisition is real. There is nothing to reverse here.
        </div>
      )}

      {plan && !blocked && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={box}>
            <div style={label}>This will</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
              {plan.deals.map((d) => (
                <li key={d.id}>Reopen <b>{d.name}</b>{d.stage_name ? <> — back at {d.stage_name}</> : null}</li>
              ))}
              {plan.deals.length === 0 && <li style={{ color: 'var(--text-3)' }}>No deals were exited as Company sold.</li>}
              {plan.company?.is_acquired && <li>Take the “acquired” flag off the company</li>}
              {plan.acquisitions.map((a) => (
                <li key={a.id}>
                  Reject the acquisition record{a.acquirer_name ? <>: <b>{a.acquirer_name}</b></> : null}
                  {a.source_url && (
                    <> (<a href={a.source_url} target="_blank" rel="noopener noreferrer"
                           style={{ color: 'var(--accent-text)' }}>source</a>)</>
                  )}
                </li>
              ))}
              <li>File your reason as a note on the company{plan.deals.length === 1 ? ' and the deal' : ''}</li>
            </ul>
          </div>

          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>
              Why was it not acquired? <span style={{ color: rose }}>Required</span>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              autoFocus
              placeholder="e.g. D5 Capital is the owner's own holding company — he bought the business himself in 2023, so nothing was sold."
              style={{
                width: '100%', resize: 'vertical', padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5,
                background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit',
                border: `1px solid ${tooShort && len > 0 ? rose : 'var(--border)'}`, borderRadius: 8,
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4, lineHeight: 1.5 }}>
              Saved as a note on the record, in the deal's stage history and in the audit log.
            </div>
          </div>
        </div>
      )}
    </window.Drawer>
  );
};

Object.assign(window, { ReverseAcquisitionDrawer });
